export const BUSINESS_REGISTRATION_DEFAULT_BACK_PATH = '/features';

function normalizePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname || '/';
}

/** A different page on this origin, when the referrer is present and readable. */
function sameOriginPriorPath(referrer: string, currentUrl: string): string | null {
  if (!referrer) return null;

  let referrerUrl: URL;
  let current: URL;
  try {
    referrerUrl = new URL(referrer);
    current = new URL(currentUrl);
  } catch {
    return null;
  }

  if (referrerUrl.origin !== current.origin) return null;

  const samePage =
    normalizePath(referrerUrl.pathname) === normalizePath(current.pathname) &&
    referrerUrl.search === current.search;
  if (samePage) return null;

  return `${normalizePath(referrerUrl.pathname)}${referrerUrl.search}${referrerUrl.hash}`;
}

/**
 * Back target for /business-registration.
 * Returns -1 to follow the existing in-site history, or a path when this page
 * was opened without a prior URL on the same domain.
 */
export function resolveBusinessRegistrationBackTarget(input: {
  historyIndex: number | null | undefined;
  historyLength: number;
  referrer: string;
  currentUrl: string;
}): -1 | string {
  const hasHistoryEntry = input.historyLength > 1;
  const hasInAppHistory =
    hasHistoryEntry && typeof input.historyIndex === 'number' && input.historyIndex > 0;

  if (hasInAppHistory) return -1;

  const priorPath = sameOriginPriorPath(input.referrer, input.currentUrl);
  if (priorPath) {
    return hasHistoryEntry ? -1 : priorPath;
  }

  // A typed URL, bookmark, or new tab still has history.length > 1 because the
  // browser counts the tab's previous entry. That entry is not this domain.
  return BUSINESS_REGISTRATION_DEFAULT_BACK_PATH;
}
